<!-- src/components/admin/AdminUsers.vue -->
<template>
  <avs-card class="pb-4">
    <template v-slot:title>
      <v-toolbar flat class="mt-4 bgTable" elevation="0">
        <v-text-field
          class="mr-4"
          dense clearable hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
          @input="() => { syncRoute(); debouncedFetch() }"
        />
        <v-select
          class="mr-4"
          dense clearable hide-details multiple
          :items="typeOptions"
          v-model="typeFilter"
          item-text="text" item-value="value"
          :label="$t('Filtrer par type')"
          @change="onFiltersChange"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" x-small :color="typeColor(item.value)" text-color="white" class="mr-1">
              {{ item.text }}
            </v-chip>
            <span v-if="index === 2" class="grey--text text-caption">
              (+{{ typeFilter.length - 2 }})
            </span>
          </template>
        </v-select>
        <v-select
          class="mr-4"
          dense clearable hide-details
          :items="statusOptions"
          v-model="statusFilter"
          item-text="text" item-value="value"
          :label="$t('Statut')"
          @change="onFiltersChange"
        />
        <v-spacer />
        <v-btn small outlined color="primary" @click="fetchUsersReset" :loading="loading && page === 1">
          <v-icon left small>mdi-refresh</v-icon>{{ $t('Actualiser') }}
        </v-btn>
      </v-toolbar>
    </template>
    
    <template v-slot:content>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading && page === 1"
        :items-per-page="-1"
        hide-default-footer
        dense
        class="elevation-0 ma-4 bgTable"
        @dblclick:row="(_, { item }) => openEditor(item)"
      >
        <!-- NOM COMPLET -->
        <template #item.fullname="{ item }">
          <div class="d-flex align-center py-2">
            <v-avatar size="40" :color="getUserColor(item)" class="mr-3 elevation-2">
              <span class="white--text text-subtitle-2 font-weight-bold">
                {{ getUserInitials(item) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-bold text-capitalize">
                {{ item.fullname || item.username || '-' }}
              </div>
              <div class="text-caption grey--text">
                {{ item.email || '-' }}
              </div>
            </div>
          </div>
        </template>
        
        
        <!-- TÉLÉPHONE -->
        <template #item.phone_number="{ item }">
          <div v-if="item.phone_number" class="text-no-wrap">
            <v-icon small class="mr-1" color="primary">mdi-phone</v-icon>
            {{ item.phone_number }}
          </div>
          <span v-else class="grey--text">-</span>
        </template>
        
        <!-- TYPES -->
        <template #item.types="{ item }">
          <div class="d-flex flex-wrap">
            <v-chip 
              v-for="t in item.types" 
              :key="t" 
              x-small 
              class="mr-1 mb-1 font-weight-medium" 
              :color="typeColor(t)" 
              text-color="white"
            >
              <v-icon left x-small>{{ typeIcon(t) }}</v-icon>
              {{ mapType(t) }}
            </v-chip>
          </div>
        </template>
        
        <!-- SOCIÉTÉ -->
        <template #item.company_name="{ item }">
          <div v-if="item.company_name">
            <v-icon small class="mr-1" color="blue">mdi-domain</v-icon>
            {{ item.company_name }}
          </div>
          <span v-else class="grey--text">-</span>
        </template>
        
        <!-- DATE D'INSCRIPTION -->
        <template #item.date_joined="{ item }">
          <div class="text-no-wrap">
            <div class="text-body-2">{{ formatDate(item.date_joined) }}</div>
            <div class="text-caption grey--text">{{ formatTime(item.date_joined) }}</div>
          </div>
        </template>
        
        <!-- STATUT -->
        <template #item.is_active="{ item }">
          <v-switch
            inset dense
            v-model="item.is_active"
            :label="item.is_active ? $t('Actif') : $t('Bloqué')"
            :color="item.is_active ? 'success' : 'error'"
            @change="toggleActive(item)"
          />
        </template>
        
        <!-- ACTIONS -->
        <template #item.actions="{ item }">
          <div class="d-flex justify-center action-buttons" style="gap:4px">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :iconI="'mdi-pencil'"
                  :spantext="$t('Modifier')"
                  label
                  fab
                  small
                  color="primary"
                  @click.stop="openEditor(item)"
                />
              </template>
              <span>{{ $t('Modifier') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :iconI="'mdi-key-variant'"
                  :spantext="$t('Régénérer le mot de passe')"
                  label
                  fab
                  small
                  color="warning"
                  @click.stop="askRegeneratePassword(item)"
                />
              </template>
              <span>{{ $t('Régénérer le mot de passe') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <avs-button
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :iconI="'mdi-tag-multiple-outline'"
                  :spantext="$t('Tarif préférentiel fidélité')"
                  label
                  fab
                  small
                  color="success"
                  @click.stop="openCustomRate(item)"
                />
              </template>
              <span>{{ $t('Tarif préférentiel fidélité') }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>

      <!-- Dialog tarif preferentiel -->
      <v-dialog v-model="customRateDialog" max-width="900" persistent>
        <AdminUserCustomRateDialog
          v-if="customRateDialog && customRateUserId"
          :user-id="customRateUserId"
          @close="customRateDialog = false"
        />
      </v-dialog>

      <!-- Dialog confirmation regeneration -->
      <v-dialog v-model="confirmPwdDialog" max-width="480" persistent>
        <v-card v-if="confirmPwdDialog">
          <v-card-title class="warning white--text">
            <v-icon left dark>mdi-key-alert</v-icon>
            {{ $t('Régénérer le mot de passe') }}
          </v-card-title>
          <v-card-text class="pt-4">
            <div class="mb-2 font-weight-bold">{{ pwdTarget?.fullname || pwdTarget?.email }}</div>
            <div class="text-caption grey--text mb-3">{{ pwdTarget?.email }}</div>

            <v-alert v-if="pwdTarget && pwdTarget.is_web" type="info" outlined dense class="mb-0">
              <div class="font-weight-bold">{{ $t('Utilisateur web détecté') }}</div>
              <div class="text-caption">
                {{ $t("Un email contenant le nouveau mot de passe sera automatiquement envoyé à") }}
                <strong>{{ pwdTarget.email }}</strong>.
              </div>
            </v-alert>
            <v-alert v-else type="warning" outlined dense class="mb-0">
              {{ $t("Le nouveau mot de passe sera affiché ici. Pensez à le copier avant de fermer la fenêtre — il ne sera plus visible après.") }}
            </v-alert>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-3">
            <v-spacer />
            <v-btn text :disabled="regenerating" @click="confirmPwdDialog = false">
              {{ $t('commons.cancel') }}
            </v-btn>
            <v-btn color="warning" :loading="regenerating" @click="confirmRegeneratePassword">
              <v-icon left>mdi-key-change</v-icon>
              {{ $t('Régénérer') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog résultat (mot de passe affiché) -->
      <v-dialog v-model="resultPwdDialog" max-width="500" persistent>
        <v-card v-if="resultPwdDialog">
          <v-card-title class="success white--text">
            <v-icon left dark>mdi-check-circle</v-icon>
            {{ $t('Mot de passe régénéré') }}
          </v-card-title>
          <v-card-text class="pt-4">
            <div class="mb-2 font-weight-bold">{{ pwdResultTarget?.fullname || pwdResultTarget?.email }}</div>
            <div class="text-caption grey--text mb-3">{{ pwdResultTarget?.email }}</div>

            <v-text-field
              :value="generatedPassword"
              readonly
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-key"
              :append-icon="showPwd ? 'mdi-eye-off' : 'mdi-eye'"
              :type="showPwd ? 'text' : 'password'"
              @click:append="showPwd = !showPwd"
              class="pwd-field"
            />

            <div class="d-flex align-center mt-3" style="gap:8px">
              <v-btn small color="primary" @click="copyPassword">
                <v-icon left small>mdi-content-copy</v-icon>
                {{ copied ? $t('Copié !') : $t('Copier') }}
              </v-btn>
              <v-spacer />
              <v-chip v-if="emailSent" small color="success" text-color="white">
                <v-icon left x-small>mdi-email-check</v-icon>
                {{ $t('Email envoyé') }}
              </v-chip>
              <v-chip v-else-if="pwdResultTarget?.is_web" small color="error" text-color="white">
                <v-icon left x-small>mdi-email-alert</v-icon>
                {{ $t("Email non envoyé") }}
              </v-chip>
            </div>

            <v-alert v-if="emailError" type="error" outlined dense class="mt-3 mb-0">
              <div class="text-caption">{{ $t('Erreur envoi email') }} : {{ emailError }}</div>
            </v-alert>

            <v-alert type="warning" outlined dense class="mt-3 mb-0">
              <div class="text-caption">
                {{ $t("Ce mot de passe ne sera plus accessible après fermeture de cette fenêtre. Copiez-le maintenant.") }}
              </div>
            </v-alert>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-3">
            <v-spacer />
            <v-btn color="primary" @click="closeResultPwdDialog">
              {{ $t('Fermer') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
      <!-- Loader bas de liste -->
      <div class="py-4 d-flex justify-center" v-if="loadingMore">
        <v-progress-circular indeterminate color="primary" size="40" />
        <div class="ml-3 text-body-2 grey--text">{{ $t('Chargement...') }}</div>
      </div>
      
      <!-- Fin de liste -->
      <div
        v-if="!loading && !loadingMore && !hasMore && items.length > 0"
        class="text-center py-4"
      >
        <v-icon color="success" size="32">mdi-check-circle-outline</v-icon>
        <div class="text-body-1 font-weight-medium mt-2">
          {{ $t('Tous les utilisateurs sont affichés') }}
        </div>
        <div class="text-caption grey--text">
          {{ items.length }} {{ $t('utilisateur(s) au total') }}
        </div>
      </div>
      
      <!-- Sentinelle -->
      <div ref="sentinel" style="height: 1px;"></div>
      
      <!-- Dialog -->
      <v-dialog v-model="dialogOpen" max-width="900px" persistent>
        <AdminUserEditor
          v-if="dialogOpen"
          v-model="selected"
          @cancel="dialogOpen = false"
          @saved="onSaved"
        />
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import AdminUserEditor from "@/components/ri3/admin/AdminUserEditor.vue"
import AdminUserCustomRateDialog from "./AdminUserCustomRateDialog.vue"
import { toDatetime } from "@/utils/datetime"
import debounce from "lodash/debounce"

export default {
  name: "AdminUsers",
  components: { AdminUserEditor, AdminUserCustomRateDialog },
  
  data() {
    return {
      search: "",
      typeFilter: [],
      statusFilter: null,
      loading: false,
      loadingMore: false,
      items: [],
      selected: null,
      dialogOpen: false,
      page: 1,
      hasMore: true,
      observer: null,

      // Régénération mot de passe
      confirmPwdDialog: false,
      pwdTarget: null,

      // Tarif preferentiel fidelite
      customRateDialog: false,
      customRateUserId: null,
      regenerating: false,
      resultPwdDialog: false,
      pwdResultTarget: null,
      generatedPassword: "",
      showPwd: true,
      copied: false,
      emailSent: false,
      emailError: null,
      
      headers: [
        { text: this.$t("Utilisateur"), value: "fullname", width: "22%" },
        //{ text: this.$t("Email"), value: "email", width: "16%" },
        { text: this.$t("Téléphone"), value: "phone_number", width: "12%" },
        { text: this.$t("Type(s)"), value: "types", width: "15%" },
        { text: this.$t("Société"), value: "company_name", width: "13%" },
        { text: this.$t("Inscription"), value: "date_joined", width: "10%" },
        { text: this.$t("Statut"), value: "is_active", width: "8%" },
        { text: "", value: "actions", sortable: false, align: "center", width: "4%" },
      ],
      
      typeOptions: [
        { text: this.$t("Mobile"), value: "mobile" },
        { text: this.$t("Web"), value: "web" },
        { text: this.$t("Pro Mobile"), value: "pro_mobile" },
        { text: this.$t("Véhicule"), value: "vehicle" },
        { text: this.$t("Sans smartphone"), value: "no_smartphone" },
      ],
      
      statusOptions: [
        { text: this.$t("Tous"), value: null },
        { text: this.$t("Actif"), value: true },
        { text: this.$t("Bloqué"), value: false },
      ],
    }
  },
  
  created() {
    this.debouncedFetch = debounce(this.fetchUsersReset, 250)
  },
  
  mounted() {
    this.applyRouteFilters()
    this.fetchUsersReset()
    this.$nextTick(this.createObserver)
  },
  
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteFilters()
        this.fetchUsersReset()
      },
      deep: false,
    },
  },
  
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
  
  methods: {
    toDatetime,
    
    formatDate(datetime) {
      if (!datetime) return '-';
      const d = new Date(datetime);
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    
    formatTime(datetime) {
      if (!datetime) return '';
      const d = new Date(datetime);
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    },
    
    getUserInitials(item) {
      const name = item.fullname || item.username || 'U';
      const parts = name.split(' ').filter(Boolean);
      if (parts.length === 0) return 'U';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    
    getUserColor(item) {
      const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'purple', 'indigo', 'teal'];
      const id = item.id || 0;
      return colors[id % colors.length];
    },
    
    typeColor(t) {
      return {
        mobile: "blue",
        web: "green",
        pro_mobile: "purple",
        vehicle: "orange",
        no_smartphone: "brown",
        unknown: "grey"
      }[t] || "grey";
    },
    
    typeIcon(t) {
      return {
        mobile: "mdi-cellphone",
        web: "mdi-monitor",
        pro_mobile: "mdi-briefcase",
        vehicle: "mdi-car",
        no_smartphone: "mdi-account-circle",
        unknown: "mdi-help-circle"
      }[t] || "mdi-help-circle";
    },
    
    mapType(t) {
      return {
        mobile: this.$t("Mobile"),
        web: this.$t("Web"),
        pro_mobile: this.$t("Pro Mobile"),
        vehicle: this.$t("Véhicule"),
        no_smartphone: this.$t("Sans smartphone"),
        unknown: this.$t("Inconnu"),
      }[t] || t;
    },
    
    applyRouteFilters() {
      const q = this.$route?.query || {}
      
      if (q.type) {
        const allowed = ['mobile', 'web', 'pro_mobile', 'vehicle', 'no_smartphone']
        this.typeFilter = String(q.type)
          .split(',')
          .map(s => s.trim())
          .filter(s => allowed.includes(s))
      } else {
        this.typeFilter = []
      }
      
      if (q.is_active === 'true') this.statusFilter = true
      else if (q.is_active === 'false') this.statusFilter = false
      else this.statusFilter = null
      
      this.search = q.search || ''
    },
    
    params() {
      const p = {}
      if (this.search) p.search = this.search
      if (this.typeFilter?.length) p.type = this.typeFilter.join(",")
      if (this.statusFilter !== null) p.is_active = this.statusFilter
      p.ordering = "-date_joined"
      p.page = this.page
      return p
    },
    
    async fetchUsers({ reset = false } = {}) {
      if (reset) {
        this.loading = true
      } else {
        this.loadingMore = true
      }
      
      try {
        const data = await AdminServices.getUsers(this, this.params())
        const results = Array.isArray(data) ? data : (data.results || [])
        
        if (reset) {
          this.items = results
        } else {
          this.items = this.items.concat(results)
        }
        
        this.hasMore = !!(data && data.next)
      } catch (e) {
        if (e?.response?.status === 404) this.hasMore = false
        else console.error("Erreur chargement utilisateurs:", e)
      } finally {
        this.loading = false
        this.loadingMore = false
        this.$nextTick(() => {
          if (this.hasMore && this.observer && this.$refs.sentinel) {
            this.observer.unobserve(this.$refs.sentinel)
            this.observer.observe(this.$refs.sentinel)
          }
        })
      }
    },

    syncRoute() {
      const q = {
        ...(this.search ? { search: this.search } : {}),
        ...(this.typeFilter.length ? { type: this.typeFilter.join(',') } : {}),
      }
      if (this.statusFilter !== null) q.is_active = String(this.statusFilter)
      this.$router.replace({ name: 'AdminUsers', query: q })
    },
    
    onFiltersChange() {
      this.syncRoute()
      this.fetchUsersReset()
    },
    
    fetchUsersReset() {
      this.page = 1
      this.hasMore = true
      this.items = []
      this.fetchUsers({ reset: true })
    },
    
    createObserver() {
      const el = this.$refs.sentinel
      if (!el) return
      
      this.observer = new IntersectionObserver(
        entries => {
          const [entry] = entries
          if (entry.isIntersecting) this.loadMore()
        },
        { root: null, rootMargin: "200px", threshold: 0.1 }
      )
      
      this.observer.observe(el)
    },
    
    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.page += 1
      this.fetchUsers({ reset: false })
    },
    
    openCustomRate(item) {
      this.customRateUserId = item.id || item.pk
      this.customRateDialog = true
    },

    openEditor(item = null) {
      this.selected = item ? JSON.parse(JSON.stringify(item)) : {
        fullname: "",
        username: "",
        email: "",
        phone_number: "",
        birthdate: null,
        is_web: false,
        is_mobile: false,
        is_pro_mobile: false,
        is_vehicle: false,
        is_active: true,
      }
      this.dialogOpen = true
    },
    
    onSaved() {
      this.dialogOpen = false
      this.fetchUsersReset()
      this.$store.commit("app/message", {
        msg: this.$t("messages.success.operation"),
        color: "success"
      })
    },
    
    toggleActive(item) {
      const previous = !item.is_active
      AdminServices.updateUserStatus(this, item.id, { is_active: !!item.is_active })
        .then(() => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.operation"),
            color: "success"
          })
        })
        .catch(() => {
          item.is_active = previous
        })
    },

    // ===== Régénération mot de passe =====
    askRegeneratePassword(item) {
      this.pwdTarget = item
      this.confirmPwdDialog = true
    },

    async confirmRegeneratePassword() {
      if (!this.pwdTarget) return
      this.regenerating = true
      try {
        const res = await AdminServices.regenerateUserPassword(this, this.pwdTarget.id)
        this.generatedPassword = res.new_password
        this.emailSent = !!res.email_sent
        this.emailError = res.email_error || null
        this.pwdResultTarget = this.pwdTarget
        this.copied = false
        this.showPwd = true
        this.confirmPwdDialog = false
        this.resultPwdDialog = true
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        })
      } finally {
        this.regenerating = false
      }
    },

    async copyPassword() {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.generatedPassword)
        } else {
          const ta = document.createElement("textarea")
          ta.value = this.generatedPassword
          ta.style.position = "fixed"
          ta.style.opacity = "0"
          document.body.appendChild(ta)
          ta.focus()
          ta.select()
          document.execCommand("copy")
          document.body.removeChild(ta)
        }
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("Impossible de copier"),
          color: "error",
        })
      }
    },

    closeResultPwdDialog() {
      this.resultPwdDialog = false
      this.pwdResultTarget = null
      this.generatedPassword = ""
      this.emailSent = false
      this.emailError = null
      this.copied = false
    },
  },
}
</script>

<style scoped>
.text-capitalize {
  text-transform: capitalize;
}

.v-data-table >>> tbody tr {
  cursor: pointer;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02) !important;
}

.action-buttons .v-btn:hover {
  transform: scale(1.1);
  transition: transform 0.2s;
}
</style>